import { useState } from 'react';

export const APIDocumentation = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  const apiSections = [
    {
      id: 'auth',
      title: 'Authentication',
      endpoints: [
        {
          method: 'POST',
          path: '/api/auth/login',
          description: 'Login and receive authentication token',
          requestBody: {
            email: 'admin@example.com',
            password: 'password123'
          },
          response: {
            success: true,
            data: {
              token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
              user: {
                id: 1,
                email: 'admin@example.com',
                full_name: 'Admin User',
                role: 'admin'
              }
            }
          },
          requiresAuth: false
        },
        {
          method: 'POST',
          path: '/api/auth/logout',
          description: 'Logout current user',
          response: {
            success: true,
            message: 'Logged out successfully'
          },
          requiresAuth: true
        },
        {
          method: 'GET',
          path: '/api/auth/me',
          description: 'Get current authenticated user',
          response: {
            success: true,
            data: {
              id: 1,
              email: 'admin@example.com',
              full_name: 'Admin User',
              role: 'admin'
            }
          },
          requiresAuth: true
        }
      ]
    },
    {
      id: 'leads',
      title: 'Leads',
      endpoints: [
        {
          method: 'GET',
          path: '/api/leads',
          description: 'Get all leads with optional filters. You can filter by status using either status_id OR status_name.',
          exampleUrl: '/api/leads?status_name=חדש&limit=10',
          queryParams: {
            status_id: 'Filter by status ID (optional)',
            status_name: 'Filter by status name - e.g. "חדש" or "New" (optional). Use this instead of status_id for easier filtering.',
            search: 'Search in lead fields (optional)',
            page: 'Page number for pagination (default: 1)',
            limit: 'Number of results per page (default: 1000)'
          },
          response: {
            success: true,
            data: [
              {
                id: 'uuid',
                status_id: 1,
                field_data: {
                  full_name: 'John Doe',
                  email: 'john@example.com',
                  phone: '+1234567890'
                },
                created_at: '2025-10-27T10:00:00Z',
                action_date: '2025-10-28T14:00:00Z'
              }
            ]
          },
          requiresAuth: true
        },
        {
          method: 'GET',
          path: '/api/leads/:id',
          description: 'Get single lead by ID',
          response: {
            success: true,
            data: {
              id: 'uuid',
              status_id: 1,
              field_data: {
                full_name: 'John Doe',
                email: 'john@example.com',
                phone: '+1234567890'
              },
              created_at: '2025-10-27T10:00:00Z'
            }
          },
          requiresAuth: true
        },
        {
          method: 'POST',
          path: '/api/leads',
          description: 'Create a new lead',
          requestBody: {
            status_id: 1,
            event_id: 2,
            action_date: '2025-10-28T14:00:00Z',
            field_data: {
              full_name: 'John Doe',
              email: 'john@example.com',
              phone: '+1234567890',
              company: 'Acme Inc',
              preferred_days: ['monday', 'wednesday'],
              message_types: ['1', '2']
            }
          },
          response: {
            success: true,
            data: {
              id: 'uuid',
              status_id: 1,
              field_data: { /* ... */ },
              created_at: '2025-10-27T10:00:00Z'
            }
          },
          requiresAuth: true
        },
        {
          method: 'PUT',
          path: '/api/leads/:id',
          description: 'Update an existing lead. You can use status_id OR status_name.',
          requestBody: {
            status_name: "חדש",
            field_data: {
              full_name: 'John Doe Updated',
              email: 'john.doe@example.com',
              message_types: ["WhatsApp", "חוסר פעילות"]
            }
          },
          response: {
            success: true,
            data: {
              id: 'uuid',
              status_id: 2,
              field_data: { /* ... */ },
              updated_at: '2025-10-27T11:00:00Z'
            }
          },
          requiresAuth: true
        },
        {
          method: 'DELETE',
          path: '/api/leads/:id',
          description: 'Delete a lead',
          response: {
            success: true,
            message: 'Lead deleted successfully'
          },
          requiresAuth: true
        },
        {
          method: 'PATCH',
          path: '/api/leads/:id/status',
          description: 'Update only the status of a lead',
          requestBody: {
            status_id: 3
          },
          response: {
            success: true,
            data: { /* updated lead */ }
          },
          requiresAuth: true
        },
        {
          method: 'PUT',
          path: '/api/leads/bulk',
          description: 'Bulk update multiple leads. You can use status_id OR status_name, and message type names OR IDs.',
          requestBody: {
            lead_ids: ['uuid1', 'uuid2', 'uuid3'],
            updates: {
              status_name: "חדש",
              event_id: 5,
              action_date: '2025-10-28T14:00:00Z',
              field_data: {
                preferred_days: ['monday', 'friday'],
                message_types: ['WhatsApp', 'חוסר פעילות']
              }
            }
          },
          response: {
            success: true,
            data: {
              success: ['uuid1', 'uuid2'],
              failed: [
                { id: 'uuid3', reason: 'Not found' }
              ],
              total: 3
            },
            message: 'Successfully updated 2 of 3 leads'
          },
          requiresAuth: true
        },
        {
          method: 'DELETE',
          path: '/api/leads/bulk',
          description: 'Bulk delete multiple leads',
          requestBody: {
            lead_ids: ['uuid1', 'uuid2', 'uuid3']
          },
          response: {
            success: true,
            data: {
              success: ['uuid1', 'uuid2'],
              failed: [
                { id: 'uuid3', reason: 'Not found' }
              ],
              total: 3
            }
          },
          requiresAuth: true
        },
        {
          method: 'GET',
          path: '/api/leads/check-duplicates/:phone',
          description: 'Check for duplicate leads by phone number',
          response: {
            success: true,
            data: [
              {
                id: 'uuid',
                field_data: {
                  full_name: 'Existing Lead',
                  phone: '+1234567890'
                }
              }
            ]
          },
          requiresAuth: true
        },
        {
          method: 'GET',
          path: '/api/leads/:id/activity',
          description: 'Get activity log for a lead',
          response: {
            success: true,
            data: [
              {
                id: 1,
                lead_id: 'uuid',
                action_type: 'updated',
                description: 'Lead updated',
                created_at: '2025-10-27T10:00:00Z',
                user_name: 'Admin User'
              }
            ]
          },
          requiresAuth: true
        }
      ]
    },
    {
      id: 'statuses',
      title: 'Lead Statuses',
      endpoints: [
        {
          method: 'GET',
          path: '/api/statuses',
          description: 'Get all lead statuses',
          response: {
            success: true,
            data: [
              {
                id: 1,
                status_name: 'New',
                status_color: '#3B82F6',
                display_order: 1,
                is_active: true
              }
            ]
          },
          requiresAuth: true
        },
        {
          method: 'POST',
          path: '/api/statuses',
          description: 'Create a new status',
          requestBody: {
            status_name: 'New Status',
            status_color: '#3B82F6',
            display_order: 5
          },
          response: {
            success: true,
            data: { /* created status */ }
          },
          requiresAuth: true
        },
        {
          method: 'PUT',
          path: '/api/statuses/:id',
          description: 'Update a status',
          requestBody: {
            status_name: 'Updated Status',
            status_color: '#EF4444'
          },
          response: {
            success: true,
            data: { /* updated status */ }
          },
          requiresAuth: true
        },
        {
          method: 'DELETE',
          path: '/api/statuses/:id',
          description: 'Delete a status',
          response: {
            success: true,
            message: 'Status deleted successfully'
          },
          requiresAuth: true
        }
      ]
    },
    {
      id: 'events',
      title: 'Events',
      endpoints: [
        {
          method: 'GET',
          path: '/api/events',
          description: 'Get all events with optional filters',
          queryParams: {
            is_active: 'Filter by active status (optional)',
            upcoming_only: 'Show only upcoming events (optional)'
          },
          response: {
            success: true,
            data: [
              {
                id: 1,
                event_name: 'Trial Session',
                event_type: 'trial',
                event_date: '2025-11-01T10:00:00Z',
                location: 'Main Studio',
                capacity: 20,
                assigned_count: 15,
                is_active: true
              }
            ]
          },
          requiresAuth: true
        },
        {
          method: 'GET',
          path: '/api/events/:id',
          description: 'Get single event by ID',
          response: {
            success: true,
            data: { /* event details */ }
          },
          requiresAuth: true
        },
        {
          method: 'POST',
          path: '/api/events',
          description: 'Create a new event',
          requestBody: {
            event_name: 'New Trial Session',
            event_type: 'trial',
            event_date: '2025-11-01T10:00:00Z',
            location: 'Main Studio',
            capacity: 20,
            description: 'Beginner trial session'
          },
          response: {
            success: true,
            data: { /* created event */ }
          },
          requiresAuth: true
        },
        {
          method: 'PUT',
          path: '/api/events/:id',
          description: 'Update an event',
          requestBody: {
            event_name: 'Updated Event',
            capacity: 25
          },
          response: {
            success: true,
            data: { /* updated event */ }
          },
          requiresAuth: true
        },
        {
          method: 'DELETE',
          path: '/api/events/:id',
          description: 'Delete an event',
          response: {
            success: true,
            message: 'Event deleted successfully'
          },
          requiresAuth: true
        },
        {
          method: 'GET',
          path: '/api/events/:id/leads',
          description: 'Get all leads assigned to an event',
          response: {
            success: true,
            data: [
              {
                id: 'uuid',
                field_data: {
                  full_name: 'John Doe',
                  phone: '+1234567890'
                }
              }
            ]
          },
          requiresAuth: true
        }
      ]
    },
    {
      id: 'views',
      title: 'Views',
      endpoints: [
        {
          method: 'GET',
          path: '/api/views',
          description: 'Get all saved views for current user',
          response: {
            success: true,
            data: [
              {
                id: 'uuid',
                view_name: 'My Custom View',
                filter_criteria: {
                  activity: 'facebook'
                },
                is_default: false
              }
            ]
          },
          requiresAuth: true
        },
        {
          method: 'GET',
          path: '/api/views/:id',
          description: 'Get single view by ID',
          response: {
            success: true,
            data: { /* view details */ }
          },
          requiresAuth: true
        },
        {
          method: 'POST',
          path: '/api/views',
          description: 'Create a new view',
          requestBody: {
            view_name: 'Facebook Leads',
            filter_criteria: {
              activity: 'facebook'
            }
          },
          response: {
            success: true,
            data: { /* created view */ }
          },
          requiresAuth: true
        },
        {
          method: 'PUT',
          path: '/api/views/:id',
          description: 'Update a view',
          requestBody: {
            view_name: 'Updated View Name'
          },
          response: {
            success: true,
            data: { /* updated view */ }
          },
          requiresAuth: true
        },
        {
          method: 'PUT',
          path: '/api/views/:id/default',
          description: 'Set a view as default',
          response: {
            success: true,
            data: { /* updated view */ }
          },
          requiresAuth: true
        },
        {
          method: 'DELETE',
          path: '/api/views/:id',
          description: 'Delete a view',
          response: {
            success: true,
            message: 'View deleted successfully'
          },
          requiresAuth: true
        }
      ]
    },
    {
      id: 'users',
      title: 'Users (Admin Only)',
      endpoints: [
        {
          method: 'GET',
          path: '/api/users',
          description: 'Get all users',
          response: {
            success: true,
            data: [
              {
                id: 1,
                email: 'user@example.com',
                full_name: 'User Name',
                role: 'user',
                is_active: true
              }
            ]
          },
          requiresAuth: true
        },
        {
          method: 'POST',
          path: '/api/users',
          description: 'Create a new user',
          requestBody: {
            email: 'newuser@example.com',
            password: 'password123',
            full_name: 'New User',
            role: 'user'
          },
          response: {
            success: true,
            data: { /* created user */ }
          },
          requiresAuth: true
        },
        {
          method: 'PUT',
          path: '/api/users/:id',
          description: 'Update a user',
          requestBody: {
            full_name: 'Updated Name',
            role: 'admin'
          },
          response: {
            success: true,
            data: { /* updated user */ }
          },
          requiresAuth: true
        },
        {
          method: 'DELETE',
          path: '/api/users/:id',
          description: 'Delete a user',
          response: {
            success: true,
            message: 'User deleted successfully'
          },
          requiresAuth: true
        }
      ]
    }
  ];

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-green-100 text-green-800',
      POST: 'bg-blue-100 text-blue-800',
      PUT: 'bg-yellow-100 text-yellow-800',
      PATCH: 'bg-orange-100 text-orange-800',
      DELETE: 'bg-red-100 text-red-800'
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" dir="ltr">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          API Documentation
        </h1>
        <p className="text-gray-600">
          Complete reference for all available API endpoints
        </p>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Base URL:</strong> <code className="bg-blue-100 px-2 py-1 rounded">{apiBaseUrl}</code>
          </p>

          <div className="mt-4 pt-4 border-t border-blue-200">
            <p className="text-sm text-blue-800 font-semibold mb-2">
              How to Authenticate:
            </p>

            <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
              <li>
                Login using <code className="bg-blue-100 px-1 rounded">POST /api/auth/login</code> with your email and password
              </li>
              <li>
                Save the token from the response:
                <pre className="bg-blue-900 text-white p-2 rounded mt-1 text-xs overflow-x-auto">
{`{
  "success": true,
  "data": {
    "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
    "user": { ... }
  }
}`}
                </pre>
              </li>
              <li>
                Include the token in all subsequent requests in the Authorization header:
                <pre className="bg-blue-900 text-white p-2 rounded mt-1 text-xs overflow-x-auto">
{`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...`}
                </pre>
              </li>
            </ol>

            <div className="mt-3 p-3 bg-blue-100 rounded">
              <p className="text-xs text-blue-900 font-semibold mb-1">
                Example with cURL:
              </p>
              <pre className="text-xs text-blue-900 overflow-x-auto">
{`curl -X GET "${apiBaseUrl}/api/leads" \\
  -H "Authorization: Bearer YOUR_TOKEN_HERE" \\
  -H "Content-Type: application/json"`}
              </pre>
            </div>

            <div className="mt-3 p-3 bg-blue-100 rounded">
              <p className="text-xs text-blue-900 font-semibold mb-1">
                Example with JavaScript (fetch):
              </p>
              <pre className="text-xs text-blue-900 overflow-x-auto">
{`fetch('${apiBaseUrl}/api/leads', {
  headers: {
    'Authorization': 'Bearer YOUR_TOKEN_HERE',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data));`}
              </pre>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {apiSections.map((section) => (
          <div key={section.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
              <svg
                className={`w-6 h-6 transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSection === section.id && (
              <div className="px-6 pb-6 space-y-6">
                {section.endpoints.map((endpoint, idx) => (
                  <div key={idx} className="border-t border-gray-200 pt-6">
                    <div className="flex items-start gap-3 mb-4">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${getMethodColor(endpoint.method)}`}>
                        {endpoint.method}
                      </span>
                      <code className="flex-1 text-sm bg-gray-100 px-3 py-1 rounded font-mono">
                        {endpoint.path}
                      </code>
                      {endpoint.requiresAuth && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                          🔒 Auth Required
                        </span>
                      )}
                    </div>

                    <p className="text-gray-700 mb-4">{endpoint.description}</p>

                    {endpoint.exampleUrl && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                        <p className="text-xs font-semibold text-green-900 mb-1">
                          Example Usage:
                        </p>
                        <code className="text-sm text-green-800 break-all">
                          {apiBaseUrl}{endpoint.exampleUrl}
                        </code>
                      </div>
                    )}

                    {endpoint.queryParams && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Query Parameters:</h4>
                        <div className="bg-gray-50 p-3 rounded">
                          {Object.entries(endpoint.queryParams).map(([key, value]) => (
                            <div key={key} className="text-sm">
                              <code className="text-blue-600">{key}</code>: {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {endpoint.requestBody && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">Request Body:</h4>
                          <button
                            onClick={() => copyToClipboard(JSON.stringify(endpoint.requestBody, null, 2))}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Copy
                          </button>
                        </div>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto text-sm">
                          <code>{JSON.stringify(endpoint.requestBody, null, 2)}</code>
                        </pre>
                      </div>
                    )}

                    {endpoint.response && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">Response:</h4>
                          <button
                            onClick={() => copyToClipboard(JSON.stringify(endpoint.response, null, 2))}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Copy
                          </button>
                        </div>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto text-sm">
                          <code>{JSON.stringify(endpoint.response, null, 2)}</code>
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
